import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { authService } from "@/api";
import { canUserAccessRoute } from "@/utils/routeAccess";

/**
 * Component that blocks access for users who shouldn't be on the current route
 * This leverages the existing navigation permission logic from Layout.jsx
 *
 * Usage: Wrap around route content or use at the top level of routing
 * <BlockAccessForIncorrectUserType>
 *   {children}
 * </BlockAccessForIncorrectUserType>
 */
export default function BlockAccessForIncorrectUserType({ children }) {
  const location = useLocation();
  const queryClient = useQueryClient();
  const {
    user,
    isLoading,
    isAuthenticated,
    hasAnyPermission,
    navigationPermissions,
  } = useAuth();

  useEffect(() => {
    const handleUnauthorizedAccess = async () => {
      try {
        console.error(
          `User ${user?.email} (${user?.userType}) attempted unauthorized access to: ${location.pathname}`
        );

        // Log out the user
        await authService.logout();

        // Clear all cached data
        queryClient.clear();

        // Redirect to login with unauthorized reason
        const params = new URLSearchParams({ reason: "unauthorized" });
        window.location.href = `/login?${params.toString()}`;
      } catch (error) {
        console.error("Error handling unauthorized access:", error);
        window.location.href = "/login";
      }
    };

    // Skip checks while loading or if not authenticated
    if (isLoading || !isAuthenticated || !user || !navigationPermissions) {
      return;
    }

    // Check if user can access current route using the same logic as navigation
    const hasAccess = canUserAccessRoute(
      location.pathname,
      user,
      navigationPermissions,
      hasAnyPermission
    );

    if (!hasAccess) {
      console.warn(
        `Blocking access: User ${user.email} (${user.userType}) cannot access ${location.pathname}`
      );
      handleUnauthorizedAccess();
    }
  }, [
    location.pathname,
    user,
    isLoading,
    isAuthenticated,
    hasAnyPermission,
    navigationPermissions,
    queryClient,
  ]);

  // Render children if user has access or while loading
  return children;
}
